import { Form, Modal } from 'antd'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import OneDriveForm from './OneDriveForm'
import { contextCompanyIdSelector } from '../../redux/context/company/selectors'
import { notificationAction } from '../../redux/middleware/actions'
import { useBackend } from '../../services/backend'
import { OneDrive } from '../../pages/settings/company/integrations/types'
import { AppDispatch } from '../../redux/store'

interface OneDriveModalProps {
  sheets: OneDrive[]
  setSheets: (sheets: OneDrive[]) => void
  sheet?: OneDrive
  modalVisible: boolean
  toggleModalVisible: () => void
  type: 'budget' | 'external' | 'actuals'
}

const OneDriveModal: React.FC<OneDriveModalProps> = ({
  modalVisible,
  toggleModalVisible,
  sheet,
  sheets,
  setSheets,
  type
}: OneDriveModalProps) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()

  const companyId = useSelector(contextCompanyIdSelector)
  const [form] = Form.useForm<OneDrive>()
  const createSheetRequest = useBackend(`/onedrive/sheets/{companyId}`, process.env.REACT_APP_INTEGRATION_URL)
  const updateSheetRequest = useBackend(`/onedrive/sheets/{companyId}/{sheetId}`, process.env.REACT_APP_INTEGRATION_URL)

  const handleSubmit = () => {
    if (form) {
      form.validateFields().then((data: OneDrive) => {
        if (!sheet) {
          createSheetRequest
            .post({
              urlParams: { companyId },
              body: {
                data
              }
            })
            .then((response: OneDrive) => {
              dispatch(
                notificationAction({
                  type: 'success',
                  message: 'CREATE_INTEGRATION_SUCCESS'
                })
              )
              setSheets([...sheets, response])
              form.resetFields()
              toggleModalVisible()
            })
            .catch(() => {
              dispatch(
                notificationAction({
                  type: 'error',
                  message: 'CREATE_INTEGRATION_ERROR',
                  description: 'VALUE_NOT_VALID'
                })
              )
            })
        } else {
          updateSheetRequest
            .put({
              urlParams: { companyId, sheetId: sheet.id },
              body: {
                data
              }
            })
            .then((response: OneDrive) => {
              dispatch(
                notificationAction({
                  type: 'success',
                  message: 'UPDATE_INTEGRATION_SUCCESS'
                })
              )
              setSheets(sheets.map(s => (s.id !== response.id ? s : response)))
              form.resetFields()
              toggleModalVisible()
            })
            .catch(() => {
              dispatch(
                notificationAction({
                  type: 'error',
                  message: 'UPDATE_INTEGRATION_ERROR',
                  description: 'VALUE_NOT_VALID'
                })
              )
            })
        }
      })
    }
  }

  useEffect(() => {
    if (sheet) {
      form.setFieldsValue(sheet)
    }
  }, [sheet])

  const handleCancel = () => {
    form.resetFields()
    toggleModalVisible()
  }

  return (
    <Modal
      width="unset"
      style={{ maxWidth: '650px' }}
      title={sheet ? t('global:update') : t('global:add-new')}
      open={modalVisible}
      onCancel={handleCancel}
      maskClosable={false}
      onOk={handleSubmit}
      confirmLoading={createSheetRequest.loading || updateSheetRequest.loading}
      okText={t('global:save')}
      cancelText={t('global:cancel')}
      destroyOnHidden
      forceRender={false}
      afterClose={() => form.resetFields()}
    >
      <Form form={form} name="oneDrive" layout="vertical" autoComplete="off">
        <OneDriveForm form={form} type={type} />
      </Form>
    </Modal>
  )
}

export default OneDriveModal
